<template>
    <!-- 쿠팡 Open API 키 — 멀티계정관리 계정 줄 하나 전용 창. 키는 확장 → 이 PC 의 couplus CLI 에 암호화해 둔다(서버로 가지 않는다).
         저장한 키 값은 다시 보여 주지 않는다(판매자코드·Access Key 끝 4자리만). -->
    <b-modal id="openApiKeyModal" :title="title" size="md" hide-footer @show="onShow">
        <div class="openapi-key">
            <p class="openapi-key__sub">
                쿠팡 윙 › 판매자정보 › 추가판매정보 › OPEN API 키 발급에서 받은 값을 넣어 주세요.
                허용 IP 에 아래 «지금 내 PC 공인 IP» 를 등록해야 작동합니다.
            </p>
            <p class="openapi-key__ip">
                지금 내 PC 공인 IP: <b>{{ ipText }}</b>
                <b-button size="sm" variant="link" class="p-0 ml-1" :disabled="busy" @click="refreshIp">다시 재기</b-button>
            </p>
            <p class="openapi-key__state" :class="'openapi-key__state--' + state.tone" role="status">
                상태: {{ state.text }}<span v-if="status && status.accessKeyTail"> · Access Key 끝 {{ status.accessKeyTail }}</span>
            </p>
            <label class="openapi-key__label" for="openApiVendor">판매자코드</label>
            <input id="openApiVendor" type="text" class="form-control form-control-sm" :value="vendorId" disabled>
            <label class="openapi-key__label" for="openApiAccess">Access Key</label>
            <input id="openApiAccess" type="text" class="form-control form-control-sm" v-model.trim="accessKey"
                   :placeholder="status && status.registered ? '저장됨 · 바꿀 때만 입력' : 'Access Key'" autocomplete="off" :disabled="busy">
            <label class="openapi-key__label" for="openApiSecret">Secret Key</label>
            <input id="openApiSecret" type="password" class="form-control form-control-sm" v-model.trim="secretKey"
                   :placeholder="status && status.registered ? '저장됨 · 바꿀 때만 입력' : 'Secret Key'" autocomplete="new-password" :disabled="busy">
            <p v-if="message" class="openapi-key__msg" :class="{ 'openapi-key__msg--bad': messageBad }" role="alert">{{ message }}</p>
            <details v-if="detail" class="openapi-key__detail">
                <summary>쿠팡 원문 보기</summary>
                <span class="openapi-key__detail-text">{{ detail }}</span>
            </details>
            <div class="openapi-key__actions">
                <b-button v-if="status && status.registered" size="sm" variant="outline-danger" class="mr-auto" :disabled="busy" @click="remove">삭제</b-button>
                <b-button size="sm" variant="outline-primary" class="mr-1" :disabled="busy || !(status && status.registered)" @click="check">키 확인</b-button>
                <b-button size="sm" variant="primary" :disabled="busy || !accessKey || !secretKey" @click="save">저장</b-button>
            </div>
            <!-- 에이전트 쓰기 승인 방식 — 서버가 로그인한 계정 것만 읽고 바꾼다(지금 쓰는 계정 줄에서만 바꾸기) -->
            <ApprovalModeSection :current="!!(row && row.isCurrent)" />
        </div>
    </b-modal>
</template>

<script>
import ApprovalModeSection from '@/components/common/ApprovalModeSection.vue';
import { loadOpenApiKeys, saveOpenApiKey, removeOpenApiKey, checkOpenApiKey, loadPublicIp, openApiStatusOf } from '@/services/coupangAccountFlow';

// 화면에 내도 되는 글만 — 제어문자·대체문자(U+FFFD)가 많이 섞인 글(압축이 안 풀린 쿠팡 본문)은 통째로 버린다(깨진 바이트 화면 노출 0)
const isGarbled = (code) => code <= 8 || code === 11 || code === 12 || (code >= 14 && code <= 31) || (code >= 127 && code <= 159) || code === 0xfffd;
function cleanShown(v) {
    const t = typeof v === 'string' ? v.trim() : '';
    if (!t) return '';
    const kept = Array.from(t).filter(ch => !isGarbled(ch.codePointAt(0))).join('');
    const bad = t.length - kept.length;
    if (bad > 0 && bad / t.length > 0.02) return '';
    return kept;
}

export default {
    name: 'OpenApiKeyModal',
    components: { ApprovalModeSection },
    props: {
        // 계정 줄(services/coupangAccountFlow.js makeRow) — 판매자코드·쿠플러스 계정만 읽는다
        row: {
            type: Object,
            default: null,
        },
    },
    data() {
        return { status: null, accessKey: '', secretKey: '', busy: false, message: '', messageBad: false, detail: '', ip: undefined };
    },
    computed: {
        vendorId() {
            return String((this.row && this.row.suppliercode) || '').toUpperCase();
        },
        title() {
            const who = this.row && (this.row.companyid || this.row.email);
            return `Open API 설정 — ${who ? who + ' · ' : ''}${this.vendorId}`;
        },
        state() {
            return openApiStatusOf(this.status);
        },
        ipText() {
            if (this.ip === undefined) return '재는 중';
            return this.ip || '못 잼';
        },
    },
    methods: {
        async onShow() {
            this.accessKey = '';
            this.secretKey = '';
            this.message = '';
            this.status = null;
            this.ip = undefined;
            await Promise.all([this.loadStatus(), this.refreshIp()]);
        },
        async loadStatus() {
            try {
                const map = await loadOpenApiKeys();
                if (!map) { this.say('쿠플러스 확장 또는 couplus 연결 프로그램이 응답하지 않습니다.', true); return; }
                this.status = map[this.vendorId] || { registered: false };
                this.$emit('changed', map);
            } catch (e) {
                this.say('상태를 확인하지 못했습니다.', true);
            }
        },
        async refreshIp() {
            try {
                const d = await loadPublicIp(this.ip !== undefined);
                this.ip = d && d.ok ? (d.ip || null) : null;
            } catch (e) {
                this.ip = null;
            }
        },
        say(text, bad, detail) {
            const shown = cleanShown(text);
            // 깨진 글이라 통째로 버려졌으면 빈 줄 대신 짧은 안내를 보인다
            this.message = shown || (text && bad ? '키를 확인하지 못했습니다(쿠팡 응답을 읽을 수 없었습니다). 잠시 뒤 다시 시도해 주세요.' : '');
            this.messageBad = !!bad;
            this.detail = cleanShown(detail);   // 쿠팡 원문 — 접어서만 보인다
        },
        async save() {
            this.busy = true;
            try {
                const r = await saveOpenApiKey(this.vendorId, this.accessKey, this.secretKey);
                if (!r.ok) { this.say(r.message || r.error || '저장하지 못했습니다.', true); return; }
                this.accessKey = '';
                this.secretKey = '';
                this.say('저장했습니다. «키 확인» 으로 작동하는지 보세요.');
                await this.loadStatus();
            } catch (e) {
                this.say('저장하지 못했습니다.', true);
            } finally {
                this.busy = false;
            }
        },
        async check() {
            this.busy = true;
            this.say('쿠팡에 조회 한 번으로 확인하는 중입니다…');
            try {
                const r = await checkOpenApiKey(this.vendorId);
                if (r.publicIp !== undefined) this.ip = r.publicIp || null;
                if (r.ready) this.say(r.message || '쿠팡 Open API 키가 작동합니다.');
                else this.say(r.message || r.error || '키를 확인하지 못했습니다.', true, r.detail);
                await this.loadStatus();
            } catch (e) {
                this.say('키를 확인하지 못했습니다.', true);
            } finally {
                this.busy = false;
            }
        },
        async remove() {
            if (!confirm(`${this.vendorId} 의 Open API 키를 이 PC 에서 지울까요?`)) return;
            this.busy = true;
            try {
                const r = await removeOpenApiKey(this.vendorId);
                if (!r.ok) { this.say(r.message || r.error || '지우지 못했습니다.', true); return; }
                this.say('지웠습니다.');
                await this.loadStatus();
            } catch (e) {
                this.say('지우지 못했습니다.', true);
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.openapi-key__detail {
    margin: 0 0 8px;
    font-size: 12px;
    color: #6c757d;
    summary { cursor: pointer; }
    .openapi-key__detail-text { display: block; margin-top: 4px; word-break: break-all; }
}
.openapi-key {
    &__sub { font-size: 12px; color: #6c757d; margin-bottom: 8px; }
    &__ip { font-size: 13px; margin-bottom: 4px; }
    &__state { font-size: 13px; margin-bottom: 8px; }
    &__state--ok { color: #1e7e34; }
    &__state--bad { color: #c82333; }
    &__label { font-size: 12px; margin: 8px 0 2px; display: block; }
    &__msg { font-size: 12px; margin-top: 8px; white-space: pre-wrap; }
    &__msg--bad { color: #c82333; }
    &__actions { display: flex; justify-content: flex-end; margin-top: 12px; }
}
</style>
